import type { CoachBranding } from '@cf-shared/data-model/coach';
import type { CoachBrandingThemeService } from './coach-branding-theme.service';

/**
 * The part of CoachBrandingThemeService this state machine uses. Declared as a
 * narrow structural type so a unit test can hand in a plain fake object without
 * pulling in Angular or the DOM.
 */
export type BrandingThemeWriter = Pick<
  CoachBrandingThemeService,
  'applyBranding' | 'clearBranding' | 'getCachedBranding'
>;

/**
 * The three coach-relationship reads pickActiveBranding() needs. They are
 * functions rather than plain values because the original code only performed
 * the second and third read when the first came back empty, and each of them
 * loads a document — so evaluating them all up front would be extra work on
 * every boot.
 */
export interface CoachRelationshipBrandingReader {
  asClientBranding(): CoachBranding | undefined;
  asCoachBranding(): CoachBranding | undefined;
  isManagingClient(): boolean;
}

/**
 * Decide which branding the app should show (#3707).
 *
 * - Prefer the as-client branding. In normal client mode that is the client's
 *   own coach; in coach-manage mode the userDb points at the managed client's
 *   DB, so it is that client's brand (which IS the coach's own brand, fanned
 *   out at save).
 * - When NOT managing a client, fall back to the coach's own as-coach branding,
 *   so a COACH sees their own brand applied across their own app (they want to
 *   know what it looks like).
 *
 * Both singletons are initialized empty on DB setup, so a non-coach simply gets
 * undefined from both and stays on MH.
 *
 * The as-coach fallback is gated on not managing a client because, while
 * managing, reading as-coach would surface the managed client's OWN coaching
 * brand (if they are also a coach) onto the managing coach's screen — a
 * cross-account leak. While managing, an empty as-client correctly stays MH.
 */
export function pickActiveBranding(
  reader: CoachRelationshipBrandingReader,
): CoachBranding | undefined {
  const asClient = reader.asClientBranding();
  if (asClient) return asClient;
  if (reader.isManagingClient()) return undefined;
  return reader.asCoachBranding();
}

/**
 * Read the coach-relationship documents and pick the branding to show, falling
 * back to the plain MH brand when a read fails.
 *
 * `reportProblem` is the caller's way of surfacing that failure — the Angular
 * service passes `LoggingService.trackSilentProblem`. Taking it as a callback is
 * what lets this live here rather than in the service: a unit test can hand in a
 * recording function, where importing the service would pull in the user
 * database and, through it, PouchDB, which does not load under the test runner.
 */
export function resolveActiveBranding(
  reader: CoachRelationshipBrandingReader,
  reportProblem: (name: string, context: Record<string, unknown>) => void,
): CoachBranding | undefined {
  try {
    return pickActiveBranding(reader);
  } catch (err) {
    // getSingleton can throw if a singleton is not yet registered. surface it
    // and fall back to MH (no branding) rather than breaking boot. mirrors the
    // other readers of these singletons.
    //
    // this report used to be named 'AppComponent: resolveActiveBranding read
    // threw', because the code lived in AppComponent. it was renamed when the
    // code moved into CoachBrandingStateService, so the Sentry title points at
    // the file that actually raises it. occurrences up to and including v1.31.8
    // carry the old name — search Sentry for it to find them.
    //
    // the name deliberately still says CoachBrandingStateService even though the
    // code now sits here: it has been renamed once already, and renaming it a
    // second time would split the Sentry history again for no gain.
    reportProblem('CoachBrandingStateService: resolveActiveBranding read threw', {
      err: (err as Error)?.message || String(err),
    });
    return undefined;
  }
}

/**
 * The cheap identity of a branding value: the three fields that actually change
 * what the user sees. Used to skip re-theming on DB changes that did not touch
 * the branding document.
 */
export function brandingChangeKey(branding: CoachBranding | undefined): string {
  return `${branding?.accentColorHex ?? ''}|${branding?.logoUrl ?? ''}|${branding?.brandName ?? ''}`;
}

/**
 * Everything the app shell needs to know about coach white-labeling (#3707),
 * with no Angular and no user-database dependency: which branding is currently
 * themed onto the document, and the logo / brand name the shell renders beside
 * it.
 *
 * This is the plain half of `CoachBrandingStateService`, which adds the Angular
 * wiring and the user-database reads on top. It is split out so the behavior
 * can be unit tested — a test cannot import the user-database service, because
 * loading it pulls in PouchDB, which does not run under the node test runner.
 *
 * Lifted out of `AppComponent` unchanged; see design-docs/coach-white-labeling.md.
 */
export class CoachBrandingState {
  /** True while a coach brand is themed onto the app. Drives `cs-app.coach-branded`. */
  brandingApplied = false;
  /** Coach logo for the Tier B co-branded side-menu header. */
  brandLogoUrl = '';
  /** Coach brand name for the Tier B co-branded side-menu header. */
  brandName = '';
  /**
   * Tier C: coach logo shown on the in-app splash on warm boots, read from the
   * localStorage cache before the user DB loads. Empty on the first-ever cold
   * boot (no cache) and after sign-out (cache wiped) → MH logo.
   */
  splashLogoUrl = '';

  /**
   * Called whenever a change here needs the app shell to re-render. The owner
   * (AppComponent) sets this to its own markForCheck. Left null in tests.
   */
  onChanged: (() => void) | null = null;

  /**
   * The branding (accent|logo|name) last applied by the reactive re-apply path.
   * Lets reapplyIfChanged() skip unrelated DB changes and re-theme only when
   * the branding document actually changes (the coach's own save syncing back,
   * or a fan-out reaching a client).
   */
  private lastReappliedBrandingKey = '';

  constructor(private theme: BrandingThemeWriter) {}

  /**
   * Tier C white-labeling (#3707): theme the in-app splash from the localStorage
   * branding cache. Runs at construction, before any DB loads. Applies the
   * cached accent (so the splash spinner + accent surfaces are branded) and the
   * cached logo (swapped into the splash in app.html). No cache → no-op, MH shows.
   */
  applySplashBrandingFromCache(): void {
    const cached = this.theme.getCachedBranding();
    if (!cached?.accentColorHex) return;
    // reuse the same apply path (derive palette + set --cs-brand-* overrides).
    this.theme.applyBranding(cached);
    this.splashLogoUrl = cached.logoUrl ?? '';
  }

  /**
   * Theme the whole app from an explicit branding value. Public so the coach
   * Branding page can call it right after a save for an instant full-app preview
   * — the server-side write that persists the as-coach doc has not synced back
   * to local PouchDB yet, so applying the in-memory value here is what makes the
   * coach see their brand immediately.
   */
  applyBrandingValue(branding: CoachBranding | undefined): void {
    this.brandingApplied = this.theme.applyBranding(branding);
    // expose the logo + name for the Tier B co-branded menu header (only when a
    // brand was actually applied, so an invalid accent falls back to pure MH).
    this.brandLogoUrl = this.brandingApplied ? (branding?.logoUrl ?? '') : '';
    this.brandName = this.brandingApplied ? (branding?.brandName ?? '') : '';
    this.onChanged?.();
  }

  /**
   * Coach white-labeling (#3707): re-theme the app when the active branding doc
   * changes. Called from the boot DocWatcher (which fires on every DB change),
   * so it is guarded by a cheap branding key — it only re-applies when the
   * accent / logo / name actually changes. This is what makes a coach's own
   * save reflect without a reload (the as-coach doc syncs back and re-applies)
   * and makes already-claimed clients re-theme on the next sync (not just the
   * next boot) when a fan-out reaches them.
   */
  reapplyIfChanged(branding: CoachBranding | undefined): void {
    const key = brandingChangeKey(branding);
    if (key === this.lastReappliedBrandingKey) return;
    this.lastReappliedBrandingKey = key;
    this.applyBrandingValue(branding);
  }

  /**
   * Drop any coach brand theme + splash cache before signing out, so the next
   * account on this device starts on pure MH. (signOut also wipes the
   * user-scoped cache key, but clearing the live DOM overrides here covers
   * paths where a full reload does not fire.)
   *
   * `splashLogoUrl` is deliberately left alone: the sign-out path is followed by
   * a full reload, and this mirrors what AppComponent did before the move.
   */
  resetForSignOut(): void {
    this.brandingApplied = false;
    this.brandLogoUrl = '';
    this.brandName = '';
    this.lastReappliedBrandingKey = '';
    this.theme.clearBranding();
  }
}
